import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAnalyticsUsage } from '../../api/client';
import type { UsageAnalyticsResponse } from '../../api/types';
import type { AnalyticsPeriod } from '../../api/types';
import { AnalyticsQueryState } from '../../analytics/AnalyticsQueryState';
import { PeriodGranularityControls } from '../../analytics/PeriodGranularityControls';
import { parsePeriod } from '../../analytics/analyticsParams';
import * as styles from './analyticsPageStyles';

export function UsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));

  const setPeriod = useCallback(
    (p: AnalyticsPeriod) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('period', String(p));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [data, setData] = useState<UsageAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getAnalyticsUsage({ period })
      .then(setData)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Unable to load usage data. Please try again.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const global = data?.global;
  const rows = data?.byWorkspace ?? [];
  const peopleAvailable = data?.peopleTrackingAvailable;

  const isEmpty =
    (global?.tracksUsed ?? 0) === 0 && (global?.tracksGranted ?? 0) === 0 && rows.length === 0;

  return (
    <div data-testid="analytics-usage-root">
      <PeriodGranularityControls period={period} onPeriodChange={setPeriod} />

      {peopleAvailable === false ? (
        <p style={styles.note} role="status">
          People-tracking metrics are not available for this environment or period (N/A). Tracks
          metrics below remain authoritative where present.
        </p>
      ) : null}

      <AnalyticsQueryState loading={loading} error={error} onRetry={load} isEmpty={isEmpty}>
        <div style={styles.card} data-testid="analytics-usage-global">
          <p style={styles.sectionTitle}>Global tracks</p>
          <div style={styles.metricGrid}>
            <div style={styles.metric}>
              <div style={styles.metricLabel}>Tracks used</div>
              <div style={styles.metricValue}>{global?.tracksUsed ?? 0}</div>
            </div>
            <div style={styles.metric}>
              <div style={styles.metricLabel}>Tracks granted</div>
              <div style={styles.metricValue}>{global?.tracksGranted ?? 0}</div>
            </div>
            <div style={styles.metric}>
              <div style={styles.metricLabel}>Utilization</div>
              <div style={styles.metricValue}>
                {global?.utilizationPercent != null ? `${global.utilizationPercent}%` : '—'}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <p style={styles.sectionTitle}>By workspace</p>
          {rows.length === 0 ? (
            <p style={styles.note}>No per-workspace usage in this period.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="analytics-usage-table">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Tracks used</th>
                    <th>Tracks granted</th>
                    <th>Utilization</th>
                    <th>People tracking</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.teamId}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{row.teamName ?? '—'}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          <code>{row.teamId}</code>
                        </div>
                      </td>
                      <td>{row.tracksUsed ?? '—'}</td>
                      <td>{row.tracksGranted ?? '—'}</td>
                      <td>{row.utilizationPercent != null ? `${row.utilizationPercent}%` : '—'}</td>
                      <td>
                        {peopleAvailable === false
                          ? 'N/A'
                          : row.peopleTracking != null
                            ? row.peopleTracking
                            : '—'}
                      </td>
                      <td>
                        <Link to={`/workspaces/${encodeURIComponent(row.teamId)}`}>View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </AnalyticsQueryState>
    </div>
  );
}
